import React, { useEffect, useRef, useState } from 'react';
import { gsap, prefersReducedMotion } from './gsap';

type CountUpProps = {
  /** Valeur affichée finale, ex: "1000+", "50K+", "99.9%", "24/7", "3%" */
  value: string;
  duration?: number;
  className?: string;
};

const NUMBER_RE = /^([\d.,]+)/;

/**
 * Compteur animé : extrait le nombre en tête de chaîne et l'anime de 0 à sa valeur
 * quand l'élément entre dans le viewport. Le reste (suffixe) est conservé tel quel.
 */
const CountUp: React.FC<CountUpProps> = ({ value, duration = 1.6, className }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const match = value.match(NUMBER_RE);
  const [display, setDisplay] = useState<string>(match ? '0' : value);

  useEffect(() => {
    const el = ref.current;
    if (!el || !match) return;

    const raw = match[1];
    const suffix = value.slice(raw.length);
    const target = parseFloat(raw.replace(',', '.'));
    const decimals = (raw.split(/[.,]/)[1] || '').length;

    // Les valeurs du type "24/7" ne sont pas des quantités : on les affiche telles quelles.
    if (Number.isNaN(target) || suffix.startsWith('/')) {
      setDisplay(value);
      return;
    }

    if (prefersReducedMotion()) {
      setDisplay(value);
      return;
    }

    const obj = { n: 0 };
    const ctx = gsap.context(() => {
      gsap.to(obj, {
        n: target,
        duration,
        ease: 'power2.out',
        onUpdate: () => {
          setDisplay(`${obj.n.toFixed(decimals)}${suffix}`);
        },
        onComplete: () => setDisplay(value),
        scrollTrigger: {
          trigger: el,
          start: 'top 90%',
          once: true,
        },
      });
    });

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default CountUp;
